/**
 * BÚSQUEDA PREDICTIVA HÍBRIDA - SKU + NOMBRE
 * ==========================================
 * 
 * Sistema de búsqueda que combina búsqueda normal (por nombre) con búsqueda
 * por SKU en el predictive search del header.
 * 
 * FUNCIONAMIENTO:
 * 1. Detecta automáticamente si el término de búsqueda es un SKU
 * 2. Si es SKU: ejecuta búsqueda normal + búsqueda por SKU en paralelo
 * 3. Si no es SKU: ejecuta solo búsqueda normal
 * 4. Combina resultados priorizando: SKU exacto > SKU parcial > búsqueda normal
 * 
 * CARACTERÍSTICAS:
 * - Limpia prefijos comunes ("SKU: ", "Código: ", etc.)
 * - Detección inteligente de SKU (evita falsos positivos)
 * - Case-insensitive (mayúsculas/minúsculas)
 * - Soporta SKUs numéricos (3-20 dígitos) y alfanuméricos (3-30 caracteres)
 * 
 * APIS UTILIZADAS:
 * - /search/suggest.json: Búsqueda predictiva nativa
 * - /products.json: Lista de productos con variantes
 */

(function() {
  'use strict';

  // Lista de palabras comunes que NO deben tratarse como SKU
  function getCommonWords() {
    return [
      // Palabras cortas (3-4 caracteres)
      'gor', 'cap', 'new', 'era', 'los', 'ang', 'yank', 'dod',
      'red', 'sox', 'cub', 'phi', 'met', 'bra', 'ast', 'roy',
      'hat', 'box', 'bag', 'set', 'kit', 'pro', 'max', 'min',
      // Nombres de productos/marcas
      'gorra', 'gorras', 'caps', 'sombrero', 'sombreros',
      'accesorio', 'accesorios', 'deporte', 'deportes',
      // Nombres de equipos/ciudades
      'angeles', 'yankee', 'dodger', 'chicago', 'boston',
      'philadelphia', 'mets', 'braves', 'astros', 'royals',
      // Marcas comunes
      'new era', 'newera', 'nike', 'adidas', 'puma'
    ];
  }

  // Remueve prefijos comunes del término de búsqueda
  function cleanSearchTerm(searchTerm) {
    let term = searchTerm.trim();
    const prefixes = [
      /^sku\s*:\s*/i,
      /^codigo\s*:\s*/i,
      /^code\s*:\s*/i,
      /^ref\s*:\s*/i,
      /^referencia\s*:\s*/i
    ];
    prefixes.forEach(prefix => term = term.replace(prefix, ''));
    return term.trim();
  }

  // Detecta si el término parece ser un SKU
  function isLikelySKU(searchTerm) {
    const term = cleanSearchTerm(searchTerm);
    
    // Validación básica: longitud 3-30 caracteres
    if (term.length < 3 || term.length > 30) return false;
    
    // Debe ser alfanumérico (letras, números, guiones, guiones bajos)
    const skuPattern = /^[A-Z0-9\-_]{3,30}$/i;
    if (!skuPattern.test(term)) return false;
    
    // Excluir palabras comunes
    const termLower = term.toLowerCase();
    const commonWords = getCommonWords();
    if (commonWords.includes(termLower)) return false;
    
    // Si contiene palabra común sin números/separadores, no es SKU
    const hasNumber = /\d/.test(term);
    const hasSeparators = /[-_]/.test(term);
    if (!hasNumber && !hasSeparators) {
      if (commonWords.some(word => word.length >= 3 && termLower.includes(word))) {
        return false;
      }
    }
    
    // Indicadores positivos: solo números, patrón SKU, o separadores
    const isOnlyNumbers = /^\d{3,20}$/.test(term);
    const hasSKUPattern = /^[A-Z]{2,}\d+|\d+[A-Z]{2,}|[A-Z]+\d+[A-Z]+|\d+[A-Z]+\d+/i.test(term);
    const isShortAlphanumeric = term.length <= 6 && /^[A-Z0-9]+$/i.test(term);
    
    return isOnlyNumbers || 
           (hasNumber && hasSeparators) || 
           (hasNumber && hasSKUPattern) || 
           (isShortAlphanumeric && hasNumber) ||
           (hasSeparators && term.length >= 4);
  }

  // Busca productos por SKU en todas las variantes y en la descripción (máx 750 productos)
  async function searchBySKUWithPagination(sku) {
    const cleanSKU = cleanSearchTerm(sku);
    const allProducts = [];
    let page = 1;
    const limit = 250;
    const maxPages = 3;
    
    try {
      while (page <= maxPages) {
        const response = await fetch(`/products.json?limit=${limit}&page=${page}`);
        if (!response.ok) break;
        
        const data = await response.json();
        if (!data.products || data.products.length === 0) break;
        
        const skuUpper = cleanSKU.toUpperCase();
        
        // Buscar en todas las variantes de cada producto y en la descripción
        data.products.forEach(product => {
          // Buscar en SKU de variantes
          const matchingVariant = product.variants.find(variant => {
            if (!variant.sku) return false;
            return variant.sku.toUpperCase().includes(skuUpper);
          });
          
          // Buscar en la descripción del producto
          const descriptionMatch = product.body_html && 
            product.body_html.toUpperCase().includes(skuUpper);
          
          // Si hay coincidencia en SKU o en descripción
          if (matchingVariant || descriptionMatch) {
            // Si hay variante con SKU coincidente, usar esa variante
            const variantToUse = matchingVariant || product.variants[0];
            const matchSource = matchingVariant ? 'sku' : 'description';
            const matchType = matchingVariant 
              ? (matchingVariant.sku.toUpperCase() === skuUpper ? 'exact' : 'partial')
              : 'partial'; // Las coincidencias en descripción siempre son parciales
            
            // Evitar duplicados
            const existingProduct = allProducts.find(p => p.id === product.id);
            if (!existingProduct) {
              allProducts.push({
                id: product.id,
                title: product.title,
                handle: product.handle,
                url: `/products/${product.handle}`,
                image: product.images?.[0]?.src || '',
                price_min: parseFloat(variantToUse.price), // El precio ya viene en pesos
                variant_id: variantToUse.id,
                sku: variantToUse.sku || '',
                _isSKUMatch: true,
                _skuMatchType: matchType,
                _matchSource: matchSource
              });
            }
          }
        });
        
        if (allProducts.length >= 10) break; // Suficientes resultados
        if (data.products.length < limit) break; // Última página
        
        page++;
      }
      
      return allProducts;
    } catch (error) {
      console.error('Error en búsqueda por SKU:', error);
      return [];
    }
  }

  // Combina resultados priorizando: SKU exacto > SKU parcial > descripción > búsqueda normal
  function mergeAndPrioritizeResults(normalResults, skuResults, maxResults = 4) {
    const merged = [];
    const seenIds = new Set();
    
    // Prioridad 1: Coincidencias exactas de SKU
    skuResults.filter(p => p._skuMatchType === 'exact').forEach(product => {
      if (!seenIds.has(product.id) && merged.length < maxResults) {
        seenIds.add(product.id);
        merged.push(product);
      }
    });
    
    // Prioridad 2: Coincidencias parciales de SKU
    skuResults.filter(p => p._skuMatchType === 'partial' && p._matchSource === 'sku').forEach(product => {
      if (!seenIds.has(product.id) && merged.length < maxResults) {
        seenIds.add(product.id);
        merged.push(product);
      }
    });
    
    // Prioridad 3: Coincidencias en descripción
    skuResults.filter(p => p._matchSource === 'description').forEach(product => {
      if (!seenIds.has(product.id) && merged.length < maxResults) {
        seenIds.add(product.id);
        merged.push(product);
      }
    });
    
    // Prioridad 4: Resultados de búsqueda normal
    normalResults.forEach(product => {
      if (!seenIds.has(product.id) && merged.length < maxResults) {
        seenIds.add(product.id);
        merged.push(product);
      }
    });
    
    return merged;
  }

  // Función principal: ejecuta búsqueda normal y por SKU/descripción en paralelo
  async function hybridPredictiveSearch(searchTerm) {
    const maxResults = 4;
    const term = searchTerm.trim();
    const cleanTerm = cleanSearchTerm(term);
    
    if (cleanTerm.length < 3) return [];
    
    // Búsqueda normal (siempre se ejecuta)
    const normalSearchPromise = fetch(
      `/search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=${maxResults}`
    )
      .then(response => response.json())
      .then(data => data.resources?.results?.products || [])
      .catch(() => []);
    
    // Búsqueda por SKU y descripción
    // Si parece SKU o es un término alfanumérico de al menos 3 caracteres, buscar también en descripción
    let skuSearchPromise = Promise.resolve([]);
    const isAlphanumeric = /^[A-Z0-9\-_\s]{3,}$/i.test(cleanTerm);
    if (isLikelySKU(term) || isAlphanumeric) {
      skuSearchPromise = searchBySKUWithPagination(term);
    }
    
    // Ejecutar ambas búsquedas en paralelo
    const [normalResults, skuResults] = await Promise.all([
      normalSearchPromise,
      skuSearchPromise
    ]);
    
    return mergeAndPrioritizeResults(normalResults, skuResults, maxResults);
  }

  // Maneja el submit del formulario para búsquedas por SKU
  function handleSearchSubmit(searchForm, searchInput) {
    if (!searchForm || !searchInput) return;
    
    searchForm.addEventListener('submit', async function(e) {
      const searchTerm = searchInput.value.trim();
      
      // Si no hay término o no parece SKU, dejar que el formulario se envíe normalmente
      if (!searchTerm || !isLikelySKU(searchTerm)) {
        return; // Permitir submit normal
      }
      
      // Si es SKU, interceptar y manejar manualmente
      e.preventDefault();
      
      const cleanTerm = cleanSearchTerm(searchTerm);
      
      // Buscar productos por SKU
      const skuResults = await searchBySKUWithPagination(searchTerm);
      
      // Filtrar solo coincidencias exactas
      const exactMatches = skuResults.filter(p => p._skuMatchType === 'exact');
      
      if (exactMatches.length === 1) {
        // Una sola coincidencia exacta: redirigir a página de búsqueda con solo ese producto
        const productId = exactMatches[0].id.toString();
        window.location.href = `/search?q=${encodeURIComponent(cleanTerm)}&sku_ids=${productId}`;
      } else if (exactMatches.length > 1) {
        // Múltiples coincidencias exactas: redirigir a búsqueda filtrada
        const productIds = exactMatches.map(p => p.id.toString()).join(',');
        window.location.href = `/search?q=${encodeURIComponent(cleanTerm)}&sku_ids=${productIds}`;
      } else if (skuResults.length > 0) {
        // Solo coincidencias parciales: redirigir a búsqueda filtrada
        const productIds = skuResults.map(p => p.id.toString()).join(',');
        window.location.href = `/search?q=${encodeURIComponent(cleanTerm)}&sku_ids=${productIds}`;
      } else {
        // No hay resultados: redirigir a búsqueda con término limpio (por si acaso)
        window.location.href = `/search?q=${encodeURIComponent(cleanTerm)}`;
      }
    });
  }

  // Inicializa el evento de búsqueda con debounce de 300ms
  function initHybridPredictiveSearch(searchInput, predictiveResults, predictiveLoading, searchForm) {
    if (!searchInput || !predictiveResults) return;
    
    let searchTimeout;
    
    // Interceptar submit del formulario para búsquedas por SKU
    if (searchForm) {
      handleSearchSubmit(searchForm, searchInput);
    }
    
    searchInput.addEventListener('input', function() {
      const searchTerm = this.value.trim();
      clearTimeout(searchTimeout);
      
      // Validación: mínimo 3 caracteres
      if (searchTerm.length < 3) {
        predictiveResults.innerHTML = '';
        if (predictiveLoading) predictiveLoading.hidden = true;
        return;
      }
      
      if (predictiveLoading) predictiveLoading.hidden = false;
      
      // Debounce: esperar 300ms antes de buscar
      searchTimeout = setTimeout(async function() {
        try {
          const products = await hybridPredictiveSearch(searchTerm);
          
          if (predictiveLoading) predictiveLoading.hidden = true;
          
          if (products.length > 0) {
            // Renderizar resultados
            let resultsHTML = '<ul class="predictive-search-list">';
            
            products.forEach(product => {
              const price = product.price_min ? new Intl.NumberFormat('es-AR', {
                style: 'currency',
                currency: 'ARS'
              }).format(product.price_min) : '';
              
              resultsHTML += `
                <li class="predictive-search-item">
                  <a href="${product.url}" class="predictive-search-link">
                    <div class="predictive-search-image">
                      <img src="${product.image || ''}" alt="${product.title}">
                    </div>
                    <div class="predictive-search-info">
                      <div class="predictive-search-title">${product.title}</div>
                      <div class="predictive-search-price">${price}</div>
                    </div>
                  </a>
                </li>
              `;
            });
            
            // Usar término limpio para el link "Ver todos"
            const cleanTerm = cleanSearchTerm(searchTerm);
            const isSKU = isLikelySKU(searchTerm);
            
            // Si es SKU, crear link especial que interceptaremos
            const viewAllHref = isSKU ? '#' : `/search?q=${encodeURIComponent(cleanTerm)}`;
            const viewAllClass = isSKU ? 'predictive-search-view-all-link sku-search-link' : 'predictive-search-view-all-link';
            const viewAllData = isSKU ? `data-sku="${encodeURIComponent(cleanTerm)}"` : '';
            
            resultsHTML += `
              <li class="predictive-search-view-all">
                <a href="${viewAllHref}" class="${viewAllClass}" ${viewAllData}>
                  Ver todos los resultados
                </a>
              </li>
            </ul>`;
            
            predictiveResults.innerHTML = resultsHTML;
            
            // Interceptar clic en "Ver todos" para búsquedas por SKU
            if (isSKU) {
              const viewAllLink = predictiveResults.querySelector('.sku-search-link');
              if (viewAllLink) {
                viewAllLink.addEventListener('click', async function(e) {
                  e.preventDefault();
                  const sku = decodeURIComponent(this.getAttribute('data-sku'));
                  
                  // Buscar productos por SKU
                  const skuResults = await searchBySKUWithPagination(sku);
                  
                  // Filtrar solo coincidencias exactas primero
                  const exactMatches = skuResults.filter(p => p._skuMatchType === 'exact');
                  
                  if (exactMatches.length === 1) {
                    // Una sola coincidencia exacta: redirigir a página de búsqueda con solo ese producto
                    const productId = exactMatches[0].id.toString();
                    window.location.href = `/search?q=${encodeURIComponent(sku)}&sku_ids=${productId}`;
                  } else if (exactMatches.length > 1) {
                    // Múltiples coincidencias exactas: redirigir a búsqueda filtrada
                    const productIds = exactMatches.map(p => p.id.toString()).join(',');
                    window.location.href = `/search?q=${encodeURIComponent(sku)}&sku_ids=${productIds}`;
                  } else if (skuResults.length > 0) {
                    // Solo coincidencias parciales: redirigir a búsqueda filtrada
                    const productIds = skuResults.map(p => p.id.toString()).join(',');
                    window.location.href = `/search?q=${encodeURIComponent(sku)}&sku_ids=${productIds}`;
                  } else {
                    // No hay resultados: redirigir a búsqueda normal
                    window.location.href = `/search?q=${encodeURIComponent(sku)}`;
                  }
                });
              }
            }
          } else {
            predictiveResults.innerHTML = '<div class="predictive-search-no-results">No se encontraron productos</div>';
          }
        } catch (error) {
          console.error('Error en búsqueda predictiva:', error);
          if (predictiveLoading) predictiveLoading.hidden = true;
          predictiveResults.innerHTML = '';
        }
      }, 300);
    });
  }

  // API pública
  window.HeaderSearchHybrid = {
    init: initHybridPredictiveSearch,
    hybridSearch: hybridPredictiveSearch,
    isLikelySKU: isLikelySKU,
    cleanSearchTerm: cleanSearchTerm
  };

})();
